import { useState, useMemo, memo } from "react";
import { Link } from "react-router-dom";
import { Activity, Link2, ChevronRight as ChevronRightIcon } from "lucide-react";
import { fmt, formatOptionName, formatFuturesName, formatExpiry } from "./utils";

// Map underlying name → slug for option contract page routing
const UNDERLYING_SLUG = {
  "NIFTY": "nifty", "BANKNIFTY": "banknifty", "FINNIFTY": "finnifty",
  "MIDCPNIFTY": "midcpnifty", "SENSEX": "sensex",
};

const FnOTab = memo(({ indexName, indexShortName, optionChainSymbol, futures, options, oiSummary, futureTicks, optionTicks, kiteAuthenticated, navigate }) => {
  const [filter, setFilter] = useState("all");
  const callOI = oiSummary?.total_call_oi || 0;
  const putOI = oiSummary?.total_put_oi || 0;
  const pcr = oiSummary?.pcr || 0;
  const nearestExpiry = oiSummary?.nearest_expiry;
  // Show OI summary if we have live OI data
  const hasLiveOI = oiSummary?.has_live_oi;

  const filteredOptions = useMemo(() => {
    if (filter === "all") return options;
    return options.filter(o => filter === "call" ? o.type === "Call" : o.type === "Put");
  }, [options, filter]);

  // Format volume/OI: show "0" for actual zero, "--" only when truly missing (null/undefined)
  const fmtVal = (v) => {
    if (v == null || v === undefined) return "--";
    if (v === 0) return "0";
    return v.toLocaleString("en-IN");
  };

  return (
    <div className="space-y-6" data-testid="fno-tab">
      {/* Option Chain Link */}
      {optionChainSymbol && (
        <Link
          to={`/option-chain?symbol=${optionChainSymbol}`}
          className="flex items-center justify-between w-full p-4 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors group"
          data-testid="fno-option-chain-link"
        >
          <div className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-[#00b386]" />
            <span className="text-sm font-bold text-gray-900">{indexName} Option Chain</span>
          </div>
          <ChevronRightIcon className="w-5 h-5 text-gray-400 group-hover:text-[#00b386] transition-colors" />
        </Link>
      )}

      {/* OI Summary — show when we have live data */}
      <div>
        <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
          Open Interest (OI)
          <Activity className="w-4 h-4 text-gray-400" />
          {nearestExpiry && <span className="text-xs text-gray-400 font-normal ml-1">Expiry: {formatExpiry(nearestExpiry)}</span>}
        </h3>
        {(callOI > 0 || putOI > 0) ? (
          <div className="grid grid-cols-3 gap-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs text-gray-500 mb-1">Total Put OI</p>
              <p className="text-lg font-bold text-gray-900">{putOI ? putOI.toLocaleString("en-IN") : "0"}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-xs text-gray-500 mb-1">Put:Call Ratio</p>
              <p className="text-lg font-bold text-gray-900">{pcr}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-right">
              <p className="text-xs text-gray-500 mb-1">Total Call OI</p>
              <p className="text-lg font-bold text-gray-900">{callOI ? callOI.toLocaleString("en-IN") : "0"}</p>
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-400 py-4 text-center border border-dashed border-gray-200 rounded-lg" data-testid="oi-unavailable">
            {!kiteAuthenticated ? "OI data temporarily unavailable" : "OI data will appear when market opens"}
          </div>
        )}
      </div>

      {/* Futures */}
      {futures.length > 0 && (
        <div>
          <h3 className="text-base font-bold text-gray-900 mb-3">{indexName} Futures</h3>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead><tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left text-xs font-medium text-gray-500 uppercase py-2.5 px-3">Contract</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase py-2.5 px-3">Last Price</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase py-2.5 px-3">OI</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase py-2.5 px-3">Volume</th>
              </tr></thead>
              <tbody>
                {futures.map(f => {
                  const tick = futureTicks[f.tradingsymbol] || {};
                  const price = tick.last_price || f.last_price || 0;
                  const oi = tick.oi ?? f.oi;
                  const vol = tick.volume ?? f.volume;
                  const humanName = formatFuturesName(f.tradingsymbol, f.name || indexShortName);
                  return (
                    <tr
                      key={f.tradingsymbol}
                      className="border-b border-gray-100 last:border-0 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => {
                        const params = new URLSearchParams({ tradingsymbol: f.tradingsymbol, exchange: f.exchange || 'NFO' });
                        navigate(`/futures-trade?${params}`);
                      }}
                      data-testid={`future-row-${f.tradingsymbol}`}
                    >
                      <td className="py-2.5 px-3">
                        <span className="text-sm font-medium text-[#00b386] hover:underline">{humanName}</span>
                        <p className="text-xs text-gray-400">{f.expiry ? formatExpiry(f.expiry) : ''}</p>
                      </td>
                      <td className="py-2.5 px-3 text-sm text-right font-medium">{price ? `₹${fmt(price)}` : "--"}</td>
                      <td className="py-2.5 px-3 text-sm text-right text-gray-600">{fmtVal(oi)}</td>
                      <td className="py-2.5 px-3 text-sm text-right text-gray-600">{fmtVal(vol)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top Options — always render if options exist */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-gray-900">Top {indexName} Options</h3>
          <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
            {["all", "put", "call"].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${filter === f ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                data-testid={`opt-filter-${f}`}
              >{f.charAt(0).toUpperCase() + f.slice(1)}</button>
            ))}
          </div>
        </div>
        {filteredOptions.length === 0 ? (
          <div className="text-sm text-gray-400 py-4 text-center border border-dashed border-gray-200 rounded-lg" data-testid="options-unavailable">
            {!kiteAuthenticated ? "Options data temporarily unavailable" : "No options data available"}
          </div>
        ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead><tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left text-xs font-medium text-gray-500 uppercase py-2.5 px-3">Option</th>
              <th className="text-right text-xs font-medium text-gray-500 uppercase py-2.5 px-3">Price</th>
              <th className="text-right text-xs font-medium text-gray-500 uppercase py-2.5 px-3">OI</th>
              <th className="text-right text-xs font-medium text-gray-500 uppercase py-2.5 px-3">Volume</th>
            </tr></thead>
            <tbody>
              {filteredOptions.map(opt => {
                const tick = optionTicks[opt.tradingsymbol] || {};
                const price = tick.last_price || opt.last_price || 0;
                const oi = tick.oi ?? opt.oi;
                const vol = tick.volume ?? opt.volume;
                const humanName = formatOptionName(indexShortName, opt.strike, opt.type);
                const underlyingSlug = UNDERLYING_SLUG[opt.name || indexShortName] || (opt.name || indexShortName).toLowerCase();
                return (
                  <tr
                    key={opt.tradingsymbol}
                    className="border-b border-gray-100 last:border-0 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/options/${underlyingSlug}/${opt.tradingsymbol}?exchange=${opt.exchange || 'NFO'}`)}
                    data-testid={`fno-option-${opt.tradingsymbol}`}
                  >
                    <td className="py-2.5 px-3">
                      <span className="text-sm font-medium text-[#00b386]">{humanName}</span>
                      {opt.expiry && <span className="text-xs text-gray-400 ml-2">{formatExpiry(opt.expiry)}</span>}
                    </td>
                    <td className="py-2.5 px-3 text-sm text-right font-medium">{price ? `₹${fmt(price)}` : "--"}</td>
                    <td className="py-2.5 px-3 text-sm text-right text-gray-600">{fmtVal(oi)}</td>
                    <td className="py-2.5 px-3 text-sm text-right text-gray-600">{fmtVal(vol)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
      </div>
    </div>
  );
});

export default FnOTab;
